import { track } from "@/features/analytics";
import { useGeneratePdfMutation } from "@/shared/services";
import { useAppSelector } from "@store";
import { getAccount } from "@store/account/selectors";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  usePdfExportStore,
  usePdfExportStoreShallow,
} from "../stores/pdf-export.store";
import { EXPORT_FORMAT_LABEL, type TExportPdfData } from "../types";
import { generatePdf, uploadToGoogleSlides } from "../utils";
import {
  dismissPdfGeneratingToast,
  pdfGeneratingToast,
} from "./pdf-generating-toast";
import { pdfReadyToast } from "./pdf-ready-toast";

/**
 * Globally mounted, headless controller for the non-blocking PDF export
 * flow.
 *
 * The share dialog only pushes an export request into the store and closes;
 * this manager runs the export in the background (so the user can keep
 * working), shows a dismissible in-progress banner while generating, and
 * fires the "Congrats" success toast once the PDF is ready.
 */
export const PdfExportManager = () => {
  const store = usePdfExportStore;
  const { status } = usePdfExportStoreShallow((s) => ({ status: s.status }));
  const account = useAppSelector(getAccount);
  // runExport is captured once by the start-export effect; read the account
  // through a ref so it is current when the export actually runs.
  const accountRef = useRef(account);
  useEffect(() => {
    accountRef.current = account;
  }, [account]);
  const { mutateAsync: handleGeneratePdf } = useGeneratePdfMutation();

  const runExport = async (pdfData: TExportPdfData) => {
    const { version } = pdfData;
    const format = pdfData.format ?? "pdf";
    const abortController = new AbortController();
    try {
      const generated = await generatePdf({
        pdfData,
        orgLogo: accountRef.current?.customization?.logo,
        abortController,
        handleGeneratePdf,
      });
      if (!generated) throw Error(`Couldn't generate ${format}`);

      // Deliberately NO version update here: a manual export is a one-off
      // download artifact. The reader-facing downloadable PDF
      // (version.data.pdf, baked into the published page) is owned by the
      // edit-version/Share save flow, which regenerates it when its options
      // change (use-update-version).

      track("success PDF export", {
        "pdf url": generated.url,
        "output format": format,
        format: pdfData.layout,
        quality: pdfData.quality,
        "high quality": pdfData.highquality,
        "min page proportion": pdfData.minPageRatio,
        "remove qr code": pdfData.removeQRCode,
        "all slides": pdfData.slides === null,
      });
      // Google Slides: the PPTX render is saved to the user's Drive. If Drive
      // refuses, the deck is still offered as a PowerPoint download.
      let openUrl: string | undefined;
      if (format === "gslides") {
        try {
          openUrl = (
            await uploadToGoogleSlides(
              generated.url,
              version.data?.title ?? version._id,
            )
          ).webViewLink;
        } catch (e) {
          track("failed Google Slides upload", {
            "error message": (e as Error).toString(),
          });
          toast.error(
            "We couldn’t save to your Google Drive. Download the PowerPoint file instead.",
          );
        }
      }
      dismissPdfGeneratingToast();
      pdfReadyToast({ pdfData, pdfObj: generated, openUrl });
    } catch (e) {
      track("failed PDF export", {
        "source url": version?.url,
        "output format": format,
        "error message": (e as Error).toString(),
      });
      dismissPdfGeneratingToast();
      toast.error(
        `We couldn’t prepare your ${EXPORT_FORMAT_LABEL[format]} file. Please try again.`,
      );
    } finally {
      store.getState().reset();
    }
  };

  // Start the export exactly once per request.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — re-run only when status changes; runExport reads the latest account via ref
  useEffect(() => {
    const s = store.getState();
    if (s.status !== "generating" || s.startedJob || !s.request) return;
    s.markStarted();
    track("view pdf export dialog", {
      "remove branding": !!s.request.removeQRCode,
      "optimize for print": !!s.request.highquality,
    });
    pdfGeneratingToast(s.request.format ?? "pdf");
    void runExport(s.request);
  }, [status]);

  return null;
};
